import React, { useState } from 'react';
import { GraduationCap, Check, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { submitInquiry } from '../lib/api';
import { openInquiryPopup } from '../lib/contact';

const hostelFeatures = [
  'Fully furnished rooms\n(2–4 students per room)',
  'Central heating, hot water\nsupply and 24/7 electricity',
  'Shared kitchen\nand laundry facilities',
  'High-speed internet access',
  'Study areas\nand common rooms',
  '24/7 security\nand CCTV surveillance',
];

const benefits = [
  'Save on accommodation costs',
  'Stay close to academic buildings',
  'Experience multicultural life',
  'Focus completely on their studies',
  'Interact with international students',
];

const privateOptions = [
  'Single or shared\napartments',
  'Fully or partially\nfurnished flats',
  'Flexible rental options',
  'Convenient locations\nnear public transport\nand campus',
];

const teamSupport = [
  'Hostel booking and\nconfirmation',
  'Guidance on hostel rules\nand facilities',
  'Assistance in finding private\napartments if required',
  'Support after arrival\nin Russia',
];

function NumBadge({ n }) {
  return (
    <div className="absolute -top-3 right-4 w-8 h-8 rounded-full bg-brand text-white text-sm font-bold flex items-center justify-center shadow-lg shadow-brand/40">
      {n}
    </div>
  );
}

function InlineForm({ variant = 'top' }) {
  const { toast } = useToast();
  const [f, setF] = useState({ name: '', phone: '', email: '', consent: false });
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.phone || !f.email) { toast({ title: 'Fill Name, Phone & Email', variant: 'destructive' }); return; }
    if (!f.consent) { toast({ title: 'Please accept privacy policy', variant: 'destructive' }); return; }
    setSubmitting(true);
    try {
      await submitInquiry({ type: 'accommodation', name: f.name, phone: f.phone, email: f.email, consent: f.consent, variant });
      toast({ title: 'Request Sent!', description: `Thanks ${f.name} — we will contact you soon.` });
      setF({ name: '', phone: '', email: '', consent: false });
    } catch (err) {
      toast({ title: 'Could not send', description: err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="pb-10">
      <div className="container mx-auto max-w-6xl px-4 md:px-8">
        <div className="bg-primary text-white rounded-2xl p-6 md:p-8 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10"
            style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath fill='%23fff' d='M50 15c-6 6-4 14 3 17 6 3 15-2 15-10 0-6-6-12-13-12-2 0-4 2-5 5zm0 30c-14 8-26 25-26 42 0 12 12 24 26 24s26-12 26-24c0-17-12-34-26-42z'/%3E%3C/svg%3E\")", backgroundSize: '120px' }} />
          <div className="relative grid md:grid-cols-[1fr_auto] gap-4 items-end">
            <div>
              <h3 className="font-serif text-2xl md:text-3xl font-bold">Contact us</h3>
              <p className="text-xs md:text-sm text-white/85 mt-1">Send a request to get a consultation.</p>
              <form onSubmit={submit} className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-2">
                <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name"
                  className="h-10 px-3 rounded-lg text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" />
                <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} type="tel" placeholder="Telephone"
                  className="h-10 px-3 rounded-lg text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" />
                <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} type="email" placeholder="Email"
                  className="h-10 px-3 rounded-lg text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" />
                <Button type="submit" className="bg-brand hover:bg-brand-dark text-white h-10 rounded-lg font-bold">Apply now</Button>
              </form>
              <label className="mt-3 flex items-start gap-2 text-[11px] text-white/80 cursor-pointer">
                <input type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} className="mt-0.5 accent-brand" />
                <span>I consent to the processing of personal data and receiving newsletters from the Russian Education, I agree with the privacy policy</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Accommodation() {
  const { toast } = useToast();
  const [f, setF] = useState({ name: '', phone: '', email: '', msg: '', consent: false });
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.phone || !f.email) { toast({ title: 'Fill Name, Phone & Email', variant: 'destructive' }); return; }
    if (!f.consent) { toast({ title: 'Please accept privacy policy', variant: 'destructive' }); return; }
    setSubmitting(true);
    try {
      await submitInquiry({ type: 'accommodation_full', name: f.name, phone: f.phone, email: f.email, message: f.msg, consent: f.consent });
      toast({ title: 'Message Sent!', description: `Thank you ${f.name} — our team will reach out soon.` });
      setF({ name: '', phone: '', email: '', msg: '', consent: false });
    } catch (err) {
      toast({ title: 'Could not send', description: err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden bg-blue-hero">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cpath fill='%23fff' fill-opacity='0.15' d='M100 20c-8 10-4 22 6 26 8 4 20-2 22-12 2-9-4-18-14-18-4 0-9 1-14 4zm0 40c-15 10-30 30-30 55 0 20 15 40 30 40s30-20 30-40c0-25-15-45-30-55zm-40 90c-15 4-30 15-30 30 0 5 3 8 8 8h124c5 0 8-3 8-8 0-15-15-26-30-30-8-2-18-4-32-4-14 0-24 2-32 4-6 1-11 0-16 0z'/%3E%3C/svg%3E\")", backgroundSize: '160px' }} />

        <div className="container mx-auto max-w-7xl px-4 md:px-8 py-14 md:py-20 relative grid md:grid-cols-[1fr_auto] gap-8 items-center">
          <div className="text-white">
            <div className="flex items-start gap-6 mb-6">
              <img src="/logo.jpg" alt="Russian Education" className="w-16 h-16 rounded-full border-2 border-white/40 bg-white p-1" />
              <div>
                <div className="text-xs md:text-sm uppercase tracking-widest text-white/80 font-semibold">your one stop gateway<br />to Russian universities</div>
              </div>
            </div>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1] tracking-tight">
              ACCOMMODATION
            </h1>
            <Button onClick={openInquiryPopup} data-testid="accom-apply-hero" className="mt-8 bg-brand hover:bg-brand-dark text-white font-bold h-12 px-8 rounded-full btn-shift">
              Apply now
            </Button>
          </div>
          <div className="hidden md:block">
            <img
              src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=500&q=80"
              alt="Student"
              className="w-80 h-80 lg:w-96 lg:h-96 object-cover rounded-2xl shadow-2xl border-4 border-white/20"
            />
          </div>
        </div>
      </section>

      {/* Main statement */}
      <section className="py-14 md:py-16">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <h2 className="font-serif text-2xl md:text-4xl lg:text-5xl font-bold text-primary leading-tight max-w-4xl">
            We understand that comfortable and safe living conditions are essential for a successful study experience abroad
          </h2>
        </div>
      </section>

      {/* Two column: text + hostel illustration */}
      <section className="pb-14">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 grid md:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-border p-7 shadow-sm">
            <p className="text-base md:text-lg text-foreground leading-relaxed font-medium">
              That is why the Russian Education helps students arrange the best accommodation options in
              Russia, so you can feel at home from your very first day
            </p>
            <p className="mt-6 text-sm text-muted-foreground leading-relaxed">
              Most of the universities provides well-equipped student hostels located close to the campus. These
              hostels are affordable, secure, and specially designed for international students.
            </p>
            <div className="mt-5 flex gap-2">
              <div className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center"><GraduationCap className="w-4 h-4" /></div>
              <div className="w-9 h-9 rounded-full bg-brand text-white flex items-center justify-center"><ShieldCheck className="w-4 h-4" /></div>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden bg-primary min-h-[320px] flex items-center justify-center p-8">
            <div className="relative">
              <div className="text-9xl">🏨</div>
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-brand text-white text-[10px] font-bold px-3 py-1 rounded uppercase tracking-widest">Hostel</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features of University Hostels */}
      <section className="py-14 md:py-16 bg-muted/50">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-10">Features of University Hostels:</h3>

          <div className="grid md:grid-cols-3 gap-4">
            {hostelFeatures.map((f, i) => (
              <div key={i} className="relative bg-white rounded-2xl border border-border p-6 shadow-sm card-lift">
                <NumBadge n={i + 1} />
                <p className="font-serif text-base md:text-lg font-semibold text-primary whitespace-pre-line leading-snug">{f}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo + Benefits list */}
      <section className="py-14 md:py-16">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl overflow-hidden bg-muted min-h-[320px]">
            <img
              src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=900&q=80"
              alt="Students"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-primary mb-6">Living in a university hostel<br />allows students to</h3>
            <ul className="space-y-3">
              {benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-brand text-white flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                  </div>
                  <span className="text-base text-foreground">{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 bg-primary text-white rounded-xl p-5">
              <p className="text-sm leading-relaxed">
                For students who prefer more privacy, we also assist in arranging private apartments or shared flats near the university.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact banner */}
      <InlineForm variant="top" />

      {/* Private Accommodation Options */}
      <section className="py-14 md:py-16 bg-muted/50">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-10">Private Accommodation Options Include:</h3>

          <div className="grid md:grid-cols-4 gap-4">
            {privateOptions.map((f, i) => (
              <div key={i} className="relative bg-white rounded-2xl border border-border p-6 shadow-sm card-lift">
                <NumBadge n={i + 1} />
                <p className="font-serif text-base md:text-lg font-semibold text-primary whitespace-pre-line leading-snug">{f}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Responsibility section */}
      <section className="py-14 md:py-16">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 grid md:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-border p-7 shadow-sm">
            <p className="font-serif text-xl md:text-2xl font-bold text-primary leading-tight">
              At the Russian Education, your comfort and safety are our responsibility
            </p>
            <p className="mt-5 text-sm text-muted-foreground leading-relaxed">
              We ensure that you receive reliable, affordable, and secure accommodation so you can focus on
              shaping your future in Medicine or Engineering with confidence.
            </p>
            <div className="mt-5 flex gap-2">
              <div className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center"><GraduationCap className="w-4 h-4" /></div>
              <div className="w-9 h-9 rounded-full bg-brand text-white flex items-center justify-center"><ShieldCheck className="w-4 h-4" /></div>
            </div>
            <Button onClick={openInquiryPopup} data-testid="accom-apply-side" className="mt-5 w-full bg-brand hover:bg-brand-dark text-white h-11 rounded-lg font-bold">
              Apply now
            </Button>
          </div>
          <div className="rounded-2xl overflow-hidden bg-muted min-h-[320px]">
            <img
              src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=900&q=80"
              alt="Students"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Team supports */}
      <section className="pb-14 md:pb-20">
        <div className="container mx-auto max-w-6xl px-4 md:px-8">
          <div className="grid md:grid-cols-[240px_1fr] gap-5 items-start">
            <h3 className="font-serif text-3xl md:text-4xl font-bold text-primary leading-tight">Our team<br />supports<br />you with:</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {teamSupport.map((s, i) => (
                <div key={i} className="relative bg-muted rounded-2xl p-5 card-lift">
                  <NumBadge n={i + 1} />
                  <p className="text-sm font-semibold text-primary whitespace-pre-line leading-snug">{s}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Us section (blue + red like About page) */}
      <section className="py-14 md:py-16">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 grid md:grid-cols-2 gap-6">
          <div className="bg-primary text-white rounded-2xl p-7 md:p-9">
            <h3 className="font-serif text-3xl md:text-4xl font-bold mb-3">Contact us</h3>
            <p className="text-sm text-white/85 leading-relaxed mb-6">
              We are always here to help you take the first step toward your international education in Russia.
            </p>
            <form onSubmit={submit} className="space-y-3">
              <textarea value={f.msg} onChange={(e) => setF({ ...f, msg: e.target.value })} placeholder="Send a message" rows={3}
                className="w-full px-3 py-2.5 rounded-lg text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" />
              <div className="grid grid-cols-3 gap-2">
                <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name"
                  className="h-10 px-3 rounded-lg text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" />
                <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} type="tel" placeholder="Telephone"
                  className="h-10 px-3 rounded-lg text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" />
                <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} type="email" placeholder="Email"
                  className="h-10 px-3 rounded-lg text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" />
              </div>
              <Button type="submit" className="w-full bg-black hover:bg-black/80 text-white h-11 rounded-lg font-bold">Apply now</Button>
              <label className="flex items-start gap-2 text-xs text-white/80 pt-2 cursor-pointer">
                <input type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} className="mt-0.5 accent-brand" />
                <span>I consent to the processing of personal data and receiving newsletters from Russian Education, I agree with the privacy policy</span>
              </label>
            </form>
          </div>

          <div className="bg-brand text-white rounded-2xl p-7 md:p-9">
            <div className="space-y-5">
              <div>
                <div className="text-xs uppercase tracking-widest text-white/75 font-semibold">Email:</div>
                <a href="mailto:admissions@russianeducation.in" className="mt-1 block text-lg font-semibold hover:underline">admissions@russianeducation.in</a>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/75 font-semibold">Phone:</div>
                  <a href="tel:+919509406802" className="mt-1 block text-sm font-bold hover:underline">+91 95094 06802</a>
                  <div className="mt-2 text-xs text-white/85">Also on WhatsApp</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/75 font-semibold">Working Hours:</div>
                  <div className="mt-1 text-xs">Monday to Friday: 10:00 AM – 7:00 PM</div>
                  <div className="mt-1 text-xs">Saturday: By appointment only</div>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/75 font-semibold">Head Office:</div>
                <div className="mt-1 text-sm font-semibold">Russian Education</div>
                <div className="text-xs text-white/85 leading-relaxed">Alex Hospital, Shiv Gate,<br />Sector 115, Noida</div>
                <div className="mt-3 text-xs uppercase tracking-widest text-white/75 font-semibold">Corporate Office:</div>
                <div className="mt-1 text-xs text-white/85 leading-relaxed">God Gift Complex, Hiran Magri,<br />Sector 3, Udaipur</div>
              </div>
              <div className="rounded-lg overflow-hidden border border-white/20">
                <iframe title="office-map" src="https://maps.google.com/maps?q=Sector%20115%20Noida&t=&z=13&ie=UTF8&iwloc=&output=embed" width="100%" height="180" style={{ border: 0 }} loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="py-4 bg-white border-t border-border">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
          <div>© 1988 — 2026, Russian Education</div>
          <div>All rights reserved</div>
        </div>
      </div>
    </div>
  );
}
